import { ChangeEventHandler, FC, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Form, Input, Row, Space, Table, Tag } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import { GENDERS_VALUES } from 'constants/users';
import { useTranslation } from 'react-i18next';
import {
  FAST_APPOINTMENTS_COLOR,
  FAST_APPOINTMENTS_LABEL,
  FAST_APPOINTMENTS_STATUS,
  FastAppointmentValues
} from 'constants/appointments';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useUIDispatchContext } from 'providers/UI';
import {
  fetchFastAppointments,
  updateFastAppointmentStatus
} from 'services/api/fastAppointments';
import {
  FastAppointmentListRow,
  UpdateFastAppointmentStatusInput
} from 'types/api/fastAppointments';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';

const PAGE_LIMIT = 10;

const PatientRequests: FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const timer = useRef<NodeJS.Timeout>();
  const page = Number(searchParams.get('page') || 1);
  const { toggleModal } = useUIDispatchContext();

  const [results, setResults] = useState<FastAppointmentListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchFastAppointments({
          page,
          limit: PAGE_LIMIT,
          name: search || undefined
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, search, refreshKey]);

  const onStatusSubmit = async (
    id: string,
    values: { note: string; status: UpdateFastAppointmentStatusInput['status'] }
  ) => {
    try {
      await updateFastAppointmentStatus(id, {
        status: values.status,
        note: values.note
      });
      toggleModal('updateStatus', false);
      setRefreshKey(k => k + 1);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const onSearch: ChangeEventHandler<HTMLInputElement> = e => {
    searchParams.set('page', '0');
    setSearchParams(searchParams);

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      setSearch(e.target.value);
    }, 700);
  };

  return (
    <PageWrapper title="Patient Requests" color="white">
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Input.Search
                placeholder={t('common.search')}
                onChange={onSearch}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'first_name',
            key: 'name',
            render(_, record) {
              return `${record.first_name} ${record.last_name}`;
            }
          },
          {
            title: 'Date of birth',
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            render(dateOfBirth: string) {
              return dayjs(dateOfBirth).format(DATE_FORMAT);
            }
          },
          {
            title: 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            render(gender: keyof typeof GENDERS_VALUES) {
              return gender
                ? t(`appointments_details.${GENDERS_VALUES[gender]}`)
                : '-';
            }
          },
          {
            title: 'Has file',
            dataIndex: 'has_file',
            key: 'files',
            render(_, record) {
              return record.files?.length ? 'yes' : 'no';
            }
          },
          {
            title: 'Contact data',
            dataIndex: 'email',
            key: 'email',
            render(_, record) {
              return (
                <>
                  <b>Phone:</b>{' '}
                  <a href={`tel:${record.phone}`}>{record.phone}</a>
                  <br />
                  <b>Email:</b>{' '}
                  <a href={`mailto:${record.email}`}>{record.email}</a>
                </>
              );
            }
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render(value: FastAppointmentValues) {
              return (
                <Tag color={FAST_APPOINTMENTS_COLOR[value]}>
                  {FAST_APPOINTMENTS_LABEL[value]}
                </Tag>
              );
            }
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render(createdAt: string) {
              return dayjs(createdAt).format(FULL_DATE_FORMAT);
            }
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'actions',
            align: 'right',
            render(id: string, record) {
              return (
                <ActionMenu
                  data={[
                    {
                      title: 'Change status',
                      permission: {
                        route: PERMISSION_ROUTES.patientRequests,
                        action: PERMISSION_ACTIONS.update
                      },
                      action() {
                        toggleModal('updateStatus', true, {
                          id,
                          currentStatus: `${record.status}`,
                          statusList: FAST_APPOINTMENTS_LABEL,
                          onSubmit: values => {
                            onStatusSubmit(id, values);
                          }
                        });
                      }
                    },
                    {
                      title: 'View',
                      action() {
                        // BE returns `id` as a number; react-router's
                        // navigate(<number>) is a history delta, not a
                        // path — so the click was a silent no-op. The
                        // legacy GQL `ID` scalar was a string, hence
                        // navigate(id) worked. Coerce back to a string.
                        navigate(String(id));
                      }
                    }
                  ]}
                  placement="bottomRight"
                  icon={<EllipsisOutlined />}
                />
              );
            }
          }
        ]}
        loading={loading}
        rowKey="id"
        rowClassName={record =>
          record.status === FAST_APPOINTMENTS_STATUS.new ? 'table-row-new' : ''
        }
        pagination={{
          total: total || 0,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          },
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default PatientRequests;
